import { keyBy, pullAt } from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import fetchApi from '../../../api'
import omit from 'lodash/omit'

const teamApi = {
    create: ({ name, colors, user }) =>
        fetchApi(`/users/${user._id}/teams`, {
            method: 'post',
            body: JSON.stringify({
                name,
                colors,
                nickName:
                    [user.firstName, user.lastName, user.email].filter(
                        Boolean
                    )[0] || '',
            }),
        }),
    update: ({ _id, user, ...team }) =>
        fetchApi(`/users/${user._id}/teams/${_id}`, {
            method: 'put',
            body: JSON.stringify(
                omit(team, [
                    'nextMatch',
                    'nextMatches',
                    'previousMatch',
                    'previousMatches',
                    'posts',
                ])
            ),
        }),

    delete: ({ teamID, userID, ...team }) =>
        fetchApi(`/users/${userID}/teams/${teamID}`, {
            method: 'delete',
        }),
    getByID: (teamID, userID) => fetchApi(`/users/${userID}/teams/${teamID}`),
    getEvents: (teamID, userID, query) =>
        fetchApi(`/users/${userID}/teams/${teamID}/events`, { query }),
    createEvent: (teamID, userID, event) =>
        fetchApi(`/users/${userID}/teams/${teamID}/events`, {
            method: 'post',
            body: event,
        }),
    getSeasons: (teamID, userID, query) =>
        fetchApi(`/users/${userID}/teams/${teamID}/seasons`, { query }),
    deletePost: (teamID, userID, postId) =>
        fetchApi(`/users/${userID}/teams/${teamID}/posts/${postId}`, {
            method: 'delete',
        }),
    getMemberInvitationLink: (teamID, userID, memberID) =>
        fetchApi(
            `/users/${userID}/teams/${teamID}/members/${memberID}/invitationLink`
        ),
}
export const TeamContext = React.createContext(null)

export const useTeam = (initialQueries = []) => {
    const teamContext = useContext(TeamContext)

    const [queries, setQueries] = useState(initialQueries)
    const [data, setData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [refetchKey, setRefetchKey] = useState()
    const refetch = () => {
        setRefetchKey(Date.now())
    }
    useEffect(() => {
        if (!queries) return
        const fetchData = async () => {
            setIsError(false)
            setIsLoading(true)

            try {
                const result = await Promise.all(
                    queries.map(async ({ method, params }) => {
                        if (!method)
                            throw new Error(`method: ${method} not set`)
                        if (!teamContext.api[method])
                            throw new Error(`method: ${method} does not exist`)

                        const r = await teamContext.api[method](params)
                        return r
                    })
                )
                setData(result)
            } catch (error) {
                console.error(error)
                setIsError(true)
            }

            setIsLoading(false)
        }

        fetchData()
    }, [queries, refetchKey])

    return [
        { isLoading, isError, ...teamContext, data },
        { refetch, setQueries },
    ]
}
export const TeamContextProvider = (props) => {
    const { user } = props
    // const initialTeam = user.teams?.[0]
    const [activeTeam, _setActiveTeam] = React.useState({})
    const [teams, setTeams] = useState(
        user?.teams?.reduce((acc, cv) => ({ [cv._id]: cv }), {}) || {}
    )
    const eventStore = {}
    const setActiveTeam = (passedTeam) =>
        _setActiveTeam((initialTeam) => {
            const team =
                typeof passedTeam === 'function'
                    ? passedTeam(initialTeam)
                    : passedTeam
            const t =
                team && Object.keys(team)
                    ? {
                          seasons: initialTeam?.seasons,
                          nextMatch: initialTeam?.nextMatch,
                          previousMatch: initialTeam?.previousMatch,
                          previousMatches: initialTeam?.previousMatches,
                          nextEvents: initialTeam?.nextEvents,
                          memberByUserIdMap: team?.members?.reduce(
                              (acc, cv) => ({
                                  [cv?.user?._id || cv.user]: cv,
                                  ...acc,
                              }),
                              {}
                          ),
                          memberMap: team?.members?.reduce(
                              (acc, cv) => ({
                                  ...(cv && {
                                      [cv._id || cv?.user?._id || cv.user]: cv,
                                  }),
                                  ...acc,
                              }),
                              {}
                          ),
                          ...team,
                      }
                    : {}
            t.eventStore = {
                ...keyBy(t?.previousMatches?.concat(t?.nextEvents), '_id'),
                ...t.eventStore,
            }
            return t
        })
    const clearactiveteam = () => setActiveTeam({})
    useEffect(() => {
        if (activeTeam && !user) {
            return setActiveTeam({})
        }
        if (!activeTeam && user) {
            return setActiveTeam(user?.teams?.[0])
        }
    }, [user, activeTeam])
    const teamUser = useMemo(
        () =>
            activeTeam?.members?.find(
                // _user is undefined for unconfirmed users
                ({ user: _user }) =>
                    _user?._id === user._id || user._id === _user
            ) || { roles: ['admin'] },
        [user, activeTeam]
    )
    const updateEventOrEvents = (field) => (eventOrEvents) => {
        const createNewEventStore = (t) =>
            [].concat(eventOrEvents).reduce((acc, event) => {
                const lineup = field === 'lineup' && event.lineups?.[t._id]
                return {
                    ...acc,
                    [event._id]: field
                        ? {
                              ...t.eventStore[event._id],
                              [field]: lineup ? lineup : event[field],
                          }
                        : event,
                }
            }, t.eventStore)
        return setActiveTeam((t) => {
            return {
                ...t,
                eventStore: createNewEventStore(t),
            }
        })
    }
    const api = {
        setActiveTeam: (ID) =>
            teamApi.getByID(ID, user._id).then(setActiveTeam),
        getByID: (_id) => teamApi.getByID(_id, user._id),
        create: (args) =>
            teamApi.create({ ...args, user }).then((t) => {
                setActiveTeam(t)
                return t
            }),
        update: (args) => teamApi.update({ ...args, user }).then(setActiveTeam),
        delete: () =>
            teamApi
                .delete({ teamID: activeTeam._id, userID: user._id })
                .then(clearActiveTeam),
        getActiveSeasons: (teamId) =>
            teamApi
                .getSeasons(teamId || activeTeam._id, user._id, {
                    // where: {
                    //     start: { $lte: Date.now() },
                    //     end: { $gte: Date.now() },
                    // },
                })
                .then((seasons) => setActiveTeam((t) => ({ ...t, seasons }))),
        getEvents: (query) =>
            teamApi.getEvents(activeTeam._id, user._id, query),
        getEventById: (eventId) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}`
            ).then(updateEventOrEvents()),
        getPosts: (options) =>
            console.log({ options }) ||
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/posts`,
                options
            ).then(
                (posts) => void setActiveTeam((t) => ({ ...t, posts })) || posts
            ),
        createPost: (post) =>
            fetchApi(`/users/${user._id}/teams/${activeTeam._id}/posts`, {
                method: 'post',
                body: post,
            }),
        getNextMatch: (teamId) =>
            teamApi
                .getEvents(teamId || activeTeam._id, user._id, {
                    where: {
                        startTime: { $gte: Date.now() },
                        eventType: { $in: ['leagueMatch', 'friendlyMatch'] },
                    },
                    sort: { startTime: 1 },
                    limit: 1,
                })
                .then(([nextMatch]) =>
                    setActiveTeam((team) => ({ ...team, nextMatch }))
                ),
        getNextEvents: (teamId) =>
            teamApi
                .getEvents(teamId || activeTeam._id, user._id, {
                    where: {
                        startTime: { $gte: Date.now() },
                        // eventType: { $in: ['leagueMatch', 'friendlyMatch'] },
                    },
                    sort: { startTime: 1 },
                    limit: 5,
                })
                .then((nextEvents) =>
                    setActiveTeam((team) => ({
                        ...team,
                        nextMatch: nextEvents[0],
                        nextEvents,
                    }))
                ),
        getPreviousMatches: (teamId) =>
            teamApi
                //
                .getEvents(teamId || activeTeam._id, user._id, {
                    where: {
                        startTime: { $lte: Date.now() },
                        eventType: { $in: ['leagueMatch', 'friendlyMatch'] },
                    },
                    sort: { startTime: -1 },
                    limit: 8,
                })
                .then((previousMatches) =>
                    setActiveTeam((team) => ({
                        ...team,
                        previousMatches,
                        previousMatch: previousMatches[0],
                    }))
                ),
        getMatchesAgainstTeam: (otherTeam) =>
            teamApi
                //
                .getEvents(activeTeam._id, user._id, {
                    where: {
                        $and: [
                            { 'teams.name': activeTeam.name },
                            { 'teams.name': otherTeam.name },
                        ],
                        // teams: {
                        //     $all: [
                        //         { $elemMatch: { team: activeTeam._id } },
                        //         { $elemMatch: { team: teamId } },
                        //     ],
                        // },
                        // { 'teams.team': teamId },
                    },
                    sort: { startTime: -1 },
                    limit: 8,
                })
                .then(
                    (results) => void updateEventOrEvents()(results) || results
                ),
        getInvitationLinkForMember: (memberId) =>
            teamApi.getMemberInvitationLink(activeTeam._id, user._id, memberId),
        deletePost: (postId) =>
            teamApi.deletePost(activeTeam._id, user._id, postId),
        getPlayerStats: () =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/playerStats`
            ),
        createEvent: (event) =>
            teamApi.createEvent(activeTeam._id, user._id, event),
        deleteEvent: (eventId) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}`,
                { method: 'delete' }
            ),

        updateEventLineup: (eventId, lineup) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}/lineup`,
                { method: 'put', body: lineup }
            ).then(updateEventOrEvents('lineups')),
        addEventLog: (eventId, eventLog) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}/logs`,
                { method: 'post', body: eventLog }
            ).then(updateEventOrEvents('logs')),
        deleteEventLog: (eventId, eventLogId) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}/logs/${eventLogId}`,
                { method: 'delete' }
            ).then(updateEventOrEvents('logs')),
        updateEventLog: (eventId, eventLog) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}/logs/${eventLog._id}`,
                { method: 'put', body: eventLog }
            ).then(updateEventOrEvents('logs')),
        updateMyAttendance: (eventId, attendance) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}/attendances`,
                {
                    method: 'put',
                    body: { ...attendance, memberId: teamUser._id },
                }
            ).then(updateEventOrEvents('attendances')),
        // updateMyAttendance: (teamID, userId, eventId, attendance) =>
        //     teamApi.updateMyAttendance(
        //         activeTeam._id,
        //         user._id,
        //         eventId,
        //         attendance
        //     ),
        updateMemberAttendance: (eventId, attendance, memberId) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/events/${eventId}/attendances`,
                { method: 'put', body: { ...attendance, memberId } }
            ).then(updateEventOrEvents('attendances')),
        addMember: (member) =>
            fetchApi(`/users/${user._id}/teams/${activeTeam._id}`, {
                method: 'put',
                body: { $push: { members: member } },
            }).then(setActiveTeam),
        updateMember: (newMember) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/members/${newMember._id}`,
                {
                    method: 'put',
                    body: newMember,
                }
            ).then(setActiveTeam),
        deleteMember: (member) =>
            fetchApi(
                `/users/${user._id}/teams/${activeTeam._id}/members/${member._id}`,
                {
                    method: 'delete',
                }
            ).then(setActiveTeam),

        getFollowers: () =>
            fetchApi(`/users/${user._id}/teams/${activeTeam._id}/followers`),
    }
    return (
        <TeamContext.Provider
            value={{
                activeTeam,
                teamUser,
                setActiveTeam,
                api,
                teams,
            }}
        >
            {props.children}
        </TeamContext.Provider>
    )
}
export default TeamContext
